import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BookOpen } from "lucide-react";
import { AgeBadge, ThemeBadge } from "@/components/yarn/Badges";
import { PageHeader } from "@/components/yarn/PageHeader";
import { YarnBall } from "@/components/yarn/YarnBall";
import { api } from "@/lib/api";

export const niceDate = (iso) => new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });

function StoryCard({ story, index }) {
  return (
    <Link to={`/story/${story.id}`} className="patch p-5 flex flex-col gap-3 rise-in hover:-translate-y-0.5 transition-transform duration-200"
      style={{ animationDelay: `${index * 50}ms` }} data-testid={`story-card-${story.id}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <ThemeBadge theme={story.theme} />
        <AgeBadge rating={story.age_rating} />
        <span className="text-xs text-[var(--yarn-muted)] ml-auto">{niceDate(story.created_at)}</span>
      </div>
      <p className="font-story text-lg leading-snug line-clamp-3">{story.opening}</p>
      <p className="text-xs text-[var(--yarn-muted)] truncate">
        <span className="font-display text-[var(--yarn-ink)]">{story.room_name}</span> · {story.line_count} lines · {story.players.join(", ")}
      </p>
    </Link>
  );
}

export default function StoriesPage() {
  const [stories, setStories] = useState(null);
  useEffect(() => {
    api.get("/stories").then((r) => setStories(r.data)).catch(() => setStories([]));
  }, []);

  return (
    <div className="flex-1" data-testid="stories-page">
      <PageHeader title="Your stories" />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <p className="font-display text-sm uppercase tracking-widest text-[var(--yarn-terracotta)]">The archive</p>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold mt-2 mb-8">Every yarn you've spun.</h1>
        {stories === null ? (
          <div className="py-16 grid place-items-center"><YarnBall size={44} className="yarn-spin" /></div>
        ) : stories.length === 0 ? (
          <div className="patch p-10 text-center" data-testid="no-stories-message">
            <BookOpen className="mx-auto text-[var(--yarn-muted)]" />
            <p className="font-display text-lg mt-3">No finished stories yet.</p>
            <p className="text-sm text-[var(--yarn-muted)] mt-1">When a story ends or restarts, it gets saved here for everyone who played.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="stories-list">
            {stories.map((s, i) => <StoryCard key={s.id} story={s} index={i} />)}
          </div>
        )}
      </main>
    </div>
  );
}
